import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import DmeTag from "@/components/DmeTag";
import { Loader2, ArrowLeft, ArrowUpRight, ArrowDownRight, Calendar } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setD(null); setNotFound(false);
    api.get(`/blog/posts/${slug}`)
      .then(r => setD(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <PublicShell>
      <div className="card-bordered p-10 text-center">
        <p className="text-[var(--text-2)] mb-4">We couldn't find that report.</p>
        <Link to="/blog" className="btn-primary inline-flex">Back to the Wire</Link>
      </div>
    </PublicShell>
  );
  if (!d) return <PublicShell><div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div></PublicShell>;

  const url = `https://dynastymetrics.com/blog/${slug}`;
  const ld = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": d.title,
    "datePublished": d.date,
    "dateModified": d.date,
    "url": url,
    "mainEntityOfPage": url,
    "description": d.description,
    "author": { "@type": "Organization", "name": "Dynasty Metrics" },
    "publisher": { "@type": "Organization", "name": "Dynasty Metrics" },
  };

  return (
    <PublicShell>
      <Helmet>
        <title>{`${d.title} | Dynasty Metrics`}</title>
        <meta name="description" content={d.description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={d.title} />
        <meta property="og:description" content={d.description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={d.date} />
        <script type="application/ld+json">{JSON.stringify(ld)}</script>
      </Helmet>

      <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-[var(--text-3)] hover:text-white mb-4" data-testid="blog-post-back"><ArrowLeft className="w-4 h-4" /> The Dynasty Wire</Link>

      <div className="grid lg:grid-cols-3 gap-6">
        <article className="lg:col-span-2 space-y-5">
          <header>
            <div className="flex items-center gap-1.5 text-[10px] tracking-widest text-[var(--text-3)] font-mono mb-2">
              <Calendar className="w-3 h-3" />{d.label || d.date}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight" data-testid="blog-post-h1">{d.title}</h1>
          </header>
          <div className="card-bordered p-6 prose-dynasty text-[var(--text-2)] leading-relaxed text-sm" data-testid="blog-post-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
              h2: ({ children }) => <h2 className="font-display text-xl font-bold text-white mt-5 mb-2 first:mt-0">{children}</h2>,
              p: ({ children }) => <p className="mb-3">{children}</p>,
              strong: ({ children }) => <strong className="text-white">{children}</strong>,
              ul: ({ children }) => <ul className="space-y-1.5 mb-3 list-disc pl-5">{children}</ul>,
            }}>{d.body_md || ""}</ReactMarkdown>
          </div>
        </article>

        <aside className="space-y-4">
          {d.risers?.length > 0 && (
            <MoverCard title="Today's Risers" items={d.risers} up testid="blog-risers" />
          )}
          {d.fallers?.length > 0 && (
            <MoverCard title="Today's Fallers" items={d.fallers} testid="blog-fallers" />
          )}
          <div className="card-bordered p-6 text-center">
            <div className="font-display text-lg font-bold mb-1">Get these alerts for your roster</div>
            <p className="text-sm text-[var(--text-2)] mb-4">Free Trade Calculator + Sleeper sync. No card required.</p>
            <Link to="/login?signup=1" className="btn-primary inline-flex w-full justify-center" data-testid="blog-post-cta">Start free</Link>
          </div>
        </aside>
      </div>

      {d.news?.length > 0 && (
        <div className="card-bordered p-5 mt-6" data-testid="blog-news">
          <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono mb-3">DYNASTY HEADLINES</div>
          <ul className="space-y-2 text-sm text-[var(--text-2)]">
            {d.news.map((n, i) => (
              <li key={i}>{n.link ? <a href={n.link} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--accent)]">{n.title}</a> : n.title}</li>
            ))}
          </ul>
        </div>
      )}
    </PublicShell>
  );
}

function MoverCard({ title, items, up, testid }) {
  return (
    <div className="card-bordered p-5" data-testid={testid}>
      <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono mb-3 inline-flex items-center gap-1">{title.toUpperCase()} <DmeTag /></div>
      <div className="space-y-1.5">
        {items.map(p => (
          <Link key={p.slug} to={`/players/${p.slug}`} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-sm hover:bg-white/5 transition-colors text-sm" data-testid={`blog-mover-${p.slug}`}>
            <span className="min-w-0 truncate hover:text-[var(--accent)]">{p.name} <span className="text-[10px] text-[var(--text-3)]">{p.position}</span></span>
            <span className={`font-mono inline-flex items-center gap-1 ${up ? "text-[var(--accent)]" : "text-[var(--negative)]"}`}>
              {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{p.change_pct > 0 ? "+" : ""}{p.change_pct}%
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
